import React, {useEffect, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowCircleRight, faEye} from "@fortawesome/free-solid-svg-icons";

import './NotificationView.css';
import {ToolTip} from "../../common/tool-tip/ToolTip";
import {loadUserProfile, updateUserNotification} from "../../../services/AccountService";
import {retrieveIndicator} from "../../../services/IndicatorService";
import {retrieveVisitWithPeople} from "../../../services/VisitService";
import {viewProblem, viewProblemOverview} from "../../../services/ProblemService";
import {viewCause} from "../../../services/CauseService";
import {viewChangeIdea} from "../../../services/ChangeIdeaService";
import {viewPDSA} from "../../../services/PDSAService";

export const NotificationView = ({
                                     context,
                                     showToaster,
                                     setSpinnerText,
                                     toolTipsEnabled,
                                     setContext,
                                     navigateTo
                                 }) => {

    const [userProfile, setUserProfile] = useState(null);
    const [userNotificationList, setUserNotificationList] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            await loadProfile();
        }
        fetchData();
    }, []);

    const loadProfile = async () => {
        setSpinnerText("Loading notifications");
        const [data, error] = await loadUserProfile(context.username);
        if (data) {
            if (data.responseStatus === "Success") {
                const userProfileRetrieved = data.userProfileVO;
                if (userProfileRetrieved) {
                    setUserProfile(userProfileRetrieved);
                    const userNotificationListRetrieved = data.userProfileVO.notificationVOS;
                    setUserNotificationList(userNotificationListRetrieved.reverse());
                }
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        setSpinnerText(null);
    }

    const handleNotificationUpdateClick = async (notificationID) => {
        const notificationVOToUpdate = {
            id: notificationID,
            systemUserExtRef: context.loggedInUser.externalReference,
            acknowledge: true,
        };
        setSpinnerText("Updating");
        const response = await updateUserNotification(context.username, notificationVOToUpdate);
        await handleResponse(response);
    }

    const handleNotificationGoToClick = async (notificationID, problemExtRef) => {
        setSpinnerText("Loading screen");
        const selectedNotification = userNotificationList.find(NotificationVO => NotificationVO.id === notificationID)
        if (selectedNotification) {
            switch (selectedNotification.targetType) {
                case "Problem":
                    await handleProblemClick(selectedNotification.targetExtRef)
                    break;
                case "Cause":
                    await handleRootCauseClick(selectedNotification.targetExtRef, problemExtRef)
                    break;
                case "PDSA":
                    await handlePDSAClick(selectedNotification.targetExtRef, problemExtRef)
                    break;
                case "Change Idea":
                    await handleChangeIdeaClick(selectedNotification.targetExtRef, problemExtRef)
                    break;
                default:
            }
        }
    }

    const handleProblemClick = async (externalReference) => {
        setSpinnerText("Retrieving Problem");
        let savedVisit = null;
        let savedIndicator = null;
        const savedProblem = await fetchProblem(externalReference);

        if (savedProblem) {
            savedVisit = await fetchVisit(savedProblem.visitExternalRef);
        }

        if (savedVisit) {
            savedIndicator = await fetchIndicator(savedVisit.indicatorExternalReference);
        }

        if (savedIndicator) {
            setContext((prevState) => ({
                ...prevState,
                viewMode: true,
                editMode: true,
                breadcrumbs: [],
                changeIdeaScreen: null,
                copyOfTopicAreaScreen: null,
                copyOfProblemScreen: null,
                copyOfRootCauseScreen: null,
                copyOfChangeIdeaScreen: null,
                problemScreen: {
                    externalReference: savedProblem.externalReference,
                    title: savedProblem.name,
                    description: savedProblem.description,
                    nextVisitDate: savedVisit.dateOfNextVisit,
                    frequency: {
                        label: savedProblem.reviewFrequency,
                        value: savedProblem.reviewFrequency
                    },
                    baselines: savedProblem.outcomeMeasureBaselines,
                    toImprove: savedProblem.aimStatementVO.toImprove,
                    from: savedProblem.aimStatementVO.from,
                    to: savedProblem.aimStatementVO.to,
                    byDate: savedProblem.aimStatementVO.by,
                },
                rootCauseScreen: null,
                topicAreaScreen: {
                    visitExternalReference: savedProblem.visitExternalRef,
                    indicator: {
                        outcomeMeasure: savedIndicator.outcomeMeasure,
                        numerator: savedIndicator.numerator,
                        denominator: savedIndicator.denominator,
                    },
                    topicArea: {
                        externalReference: savedProblem.topicAreaExternalRef,
                        topicArea: savedProblem.topicAreaName,
                    },
                    grant: {externalReference: savedProblem.grantExternalRef, name: null},
                },
                facilityScreen: {
                    levelOfSupport: {label: savedProblem.levelOfSupport},
                    locationExternalReference: savedProblem.locationExternalRef,
                },
            }));
            navigateTo("problemOne");
        }
        setSpinnerText(null);
    }

    const handleRootCauseClick = async (externalReference, problemExtRef) => {
        setSpinnerText("Retrieving Root Cause");
        let savedCause = await fetchCause(externalReference);
        const savedProblem = await fetchProblem(problemExtRef);
        if (savedCause) {
            setContext((prevState) => ({
                ...prevState,
                viewMode: true,
                editMode: true,
                breadcrumbs: [],
                changeIdeaScreen: null,
                copyOfTopicAreaScreen: null,
                copyOfProblemScreen: null,
                copyOfRootCauseScreen: null,
                copyOfChangeIdeaScreen: null,
                problemScreen: {
                    title: savedProblem.name,
                },
                problemToBeViewedExternalReference: problemExtRef,
                rootCauseScreen: {
                    externalReference: savedCause.externalReference,
                    name: savedCause.name,
                    description: savedCause.description,
                    fileList: savedCause.causeEvidenceList,
                    priority: {label: savedCause.priority, value: savedCause.priority},
                    title: savedCause.title,
                },
                topicAreaScreen: {},
                facilityScreen: null,
            }));
            navigateTo("rootCauseOne");
        }
        setSpinnerText(null);
    }

    const handlePDSAClick = async (PDSAExternalReference, problemExtRef) => {
        setSpinnerText("Retrieving PDSA");
        let savedPDSA = await fetchPDSA(PDSAExternalReference);
        let savedProblemOverview = await fetchProblemOverview(problemExtRef);
        let savedChangeIdeaTitle = null;
        let savedChangeIdeaExtRef = null;
        if (savedProblemOverview) {
            if (savedProblemOverview.routeCauseList) {
                savedProblemOverview.routeCauseList.map((rootCause) => {
                    if (rootCause.changeIdeaList) {
                        rootCause.changeIdeaList.map((changeIdea) => {
                            if (changeIdea.pdsalist) {
                                changeIdea.pdsalist.map((PDSA) => {
                                    if (PDSA.externalReference === PDSAExternalReference) {
                                        savedChangeIdeaTitle = changeIdea.name;
                                        savedChangeIdeaExtRef = changeIdea.externalReference;
                                    }
                                });
                            }
                        });
                    }
                });
            }
        }

        if (savedPDSA) {
            const mappedProcessMeasureList = savedPDSA.processMeasures.map((processMeasure) => {
                return {...processMeasure}
            });

            setContext((prevState) => ({
                ...prevState,
                viewMode: true,
                editMode: true,
                breadcrumbs: [],
                changeIdeaScreen: {
                    externalReference: savedChangeIdeaExtRef,
                    title: savedChangeIdeaTitle,
                },
                copyOfTopicAreaScreen: null,
                copyOfProblemScreen: null,
                copyOfRootCauseScreen: null,
                copyOfChangeIdeaScreen: null,
                problemScreen: {
                    externalReference: problemExtRef,
                },
                PDSAScreen: {
                    externalReference: savedPDSA.externalReference,
                    name: savedPDSA.name,
                    processMeasureList: mappedProcessMeasureList,
                    who: savedPDSA.who,
                    what: savedPDSA.what,
                    where: savedPDSA.where,
                    testStartDate: savedPDSA.testStartDate,
                    testReviewDate: savedPDSA.testReviewDate,
                    scale: savedPDSA.scale,
                    observation: savedPDSA.observations,
                    balancingMeasureOne: savedPDSA.balanceMeasureA,
                    balancingMeasureTwo: savedPDSA.balanceMeasureB,
                    balancingMeasureThree: savedPDSA.balanceMeasureC,
                    analysis: savedPDSA.analysis,
                    status: {value: savedPDSA.status, label: savedPDSA.status,},
                    statusMovement: savedPDSA.statusMovement,
                    rationale: savedPDSA.rationale,
                    nextVisitDate: savedPDSA.dateOfNextVisit,
                },
            }));
            navigateTo("PDSACycle");
        }
        setSpinnerText(null);
    }

    const handleChangeIdeaClick = async (externalReference, problemExtRef) => {
        setSpinnerText("Retrieving Change Idea");
        let savedChangeIdea = await fetchChangeIdea(externalReference);
        if (savedChangeIdea) {

            let toImprove = null;
            let from = null;
            let to = null;
            let byDate = null;

            if (savedChangeIdea.aimStatementVO) {
                toImprove = savedChangeIdea.aimStatementVO.toImprove;
                from = savedChangeIdea.aimStatementVO.from;
                to = savedChangeIdea.aimStatementVO.to;
                byDate = savedChangeIdea.aimStatementVO.by;
            }

            setContext((prevState) => ({
                ...prevState,
                viewMode: true,
                editMode: true,
                breadcrumbs: [],
                changeIdeaScreen: {
                    changeConcept: savedChangeIdea.concept,
                    priority: {label: savedChangeIdea.priority, value: savedChangeIdea.priority,},
                    description: savedChangeIdea.description,
                    prediction: savedChangeIdea.prediction,
                    toImprove,
                    from,
                    to,
                    byDate,
                    processMeasureList: savedChangeIdea.processMeasureVOList,
                    PDSAList: [],
                    externalReference: savedChangeIdea.externalReference,
                    causeExternalReference: savedChangeIdea.causeExternalReference,
                    visitExternalReference: savedChangeIdea.visitExternalReference,
                    topicAreaExternalReference: savedChangeIdea.topicAreaExternalReference,
                    title: savedChangeIdea.title,
                },
                copyOfTopicAreaScreen: null,
                copyOfProblemScreen: null,
                copyOfRootCauseScreen: null,
                copyOfChangeIdeaScreen: null,
                problemScreen: {},
                problemToBeViewedExternalReference: problemExtRef,
                rootCauseScreen: {
                    externalReference: savedChangeIdea.causeExternalReference,
                    name: savedChangeIdea.causeName,
                },
                topicAreaScreen: {},
                facilityScreen: null,
            }));
            navigateTo("changeIdeaOne");
        }
        setSpinnerText(null);
    }

    const fetchIndicator = async (indicatorExternalReference) => {
        let savedIndicator = null;
        const [data, error] = await retrieveIndicator(context.username, indicatorExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                savedIndicator = data.indicatorVOToView;
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedIndicator;
    }

    const fetchVisit = async (visitExternalReference) => {
        let savedVisit = null;
        const [data, error] = await retrieveVisitWithPeople(context.username, visitExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                savedVisit = data.visitVOToView;
            } else {
                showToaster(error);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedVisit;
    }

    const fetchProblem = async (problemExternalReference) => {
        let savedProblem = null;
        const [data, error] = await viewProblem(context.username, problemExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                savedProblem = data.problemVOToView;
            } else {
                showToaster(error);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedProblem;
    }

    const fetchProblemOverview = async (problemExternalReference) => {
        let savedProblemOverview = null;
        const [data, error] = await viewProblemOverview(context.username, problemExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                savedProblemOverview = data.problemOverview;
            } else {
                showToaster(error);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedProblemOverview;
    }

    const fetchCause = async (causeExternalReference) => {
        let savedCause = null;
        const [data, error] = await viewCause(context.username, causeExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                savedCause = data.causeVOToView;
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedCause;
    }

    const fetchChangeIdea = async (changeIdeaExternalReference) => {
        let savedChangeIdea = null;
        const [data, error] = await viewChangeIdea(context.username, changeIdeaExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                savedChangeIdea = data.changeIdeaVOToView;
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedChangeIdea;
    }

    const fetchPDSA = async (PDSAExternalReference) => {
        let savedPDSA = null;
        const [data, error] = await viewPDSA(context.username, PDSAExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                savedPDSA = data.pdsaVOToView;
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedPDSA;
    }

    const handleResponse = async ([data, error]) => {
        if (data) {
            if (data.responseStatus === "Success") {
                await loadProfile();
            }
            showToaster(data);
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        setSpinnerText(null);
    }

    return (
        <div className="wizard-content-container">
            <div className="wizard-main-content-container">
                <div className="wizard-right-container-n">
                    <div className="notification-container">
                        <div className={"label-div-tooltip-nn"}>
                            <label className={"label"}>Notifications:</label>
                            {toolTipsEnabled &&
                            <ToolTip tooltipExternalReference={"notifications123"}
                                     context={context}
                                     setSpinnerText={setSpinnerText} showToaster={showToaster}
                                     fieldName={"Notifications:"}/>}
                        </div>
                        <div className={"text-box-notification"}>
                            {userProfile !== null && userNotificationList !== null ?
                                <ul className={"comment-container-n"}>
                                    {userNotificationList.map((NotificationVO, index) => (
                                        <li key={index} className={"comment-message-container-n"}>
                                            <div>
                                                <button className={"acknowledge-notification-button"}
                                                        onClick={() => handleNotificationUpdateClick(NotificationVO.id)}>
                                                    <FontAwesomeIcon className="eye-icon" icon={faEye}/>
                                                </button>
                                                {NotificationVO.message}
                                                {NotificationVO.message.includes("roblem") &&
                                                <button className={"acknowledge-notification-button"}
                                                        onClick={() => handleNotificationGoToClick(NotificationVO.id)}>
                                                    <FontAwesomeIcon className="eye-icon"
                                                                     icon={faArrowCircleRight}/>
                                                </button>}
                                                {NotificationVO.message.includes("ause") &&
                                                <button className={"acknowledge-notification-button"}
                                                        onClick={() => handleNotificationGoToClick(NotificationVO.id, NotificationVO.problemExternalReference)}>
                                                    <FontAwesomeIcon className="eye-icon"
                                                                     icon={faArrowCircleRight}/>
                                                </button>}
                                                {NotificationVO.message.includes("PDSA") &&
                                                <button className={"acknowledge-notification-button"}
                                                        onClick={() => handleNotificationGoToClick(NotificationVO.id, NotificationVO.problemExternalReference)}>
                                                    <FontAwesomeIcon className="eye-icon"
                                                                     icon={faArrowCircleRight}/>
                                                </button>}
                                                {NotificationVO.message.includes("hange") &&
                                                <button className={"acknowledge-notification-button"}
                                                        onClick={() => handleNotificationGoToClick(NotificationVO.id, NotificationVO.problemExternalReference)}>
                                                    <FontAwesomeIcon className="eye-icon"
                                                                     icon={faArrowCircleRight}/>
                                                </button>}
                                            </div>
                                        </li>))}
                                </ul> :
                                <p className={"no-comments-message"}>No notifications yet</p>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
